import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';

import useBookableCurator from '../../hooks/useBookableCurator';
import DefaultInterfacesHandler from '../handlers/DefaultInterfacesHandler';

import { selectAppConfigProp } from '../../slices/appState';
import { useSelector } from 'react-redux';

import { motion } from 'framer-motion';

import { DateTime } from 'luxon';
import {
  Grid,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';

import useElDimensions from '../../hooks/useElDimensions';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

import { useTranslation } from 'react-i18next';
import ProductSelectorLabel from '../base/ProductSelectorLabel';
import ResourceSelectorLabel from '../base/ResourceSelectorLabel';
import usePropsBeforeContext from '../../hooks/usePropsBeforeContext';
import { CalendarViewContext } from '../Calendar';
import { CrossFade } from '../base/CrossFade';
import { LOADING_GRACE_MS, LOADING_MIN_DISPLAY_MS } from '../../hooks/useSmartLoading';
import useScrollbarWidth from '../../hooks/useScrollbarWidth';
import ResourceColorLegend from './ResourceColorLegend';
import {
  groupOverlapping,
  subGroupByUuid,
  buildColorMap,
  adaptiveFontSize,
} from '../../utils/multiResourceUtils';
// import FlickeringGrid from '../base/FlickeringGrid';

/**
 * Formats a week button label based on available width
 * @param {DateTime} mondayDate - The Monday date for the week
 * @param {number} colWidth - Column width to determine format
 * @param {Function} t - Translation function
 * @returns {string} Formatted label
 */
function formatWeekButtonLabel(mondayDate, colWidth, t) {
  const prefix = t('week_navigation_from', 'From');

  let dateFormat;
  if (colWidth >= 200) {
    // Wide: DD/MM
    dateFormat = mondayDate.toLocaleString({
      day: '2-digit',
      month: '2-digit',
    });
  } else if (colWidth >= 150) {
    // Medium: DD/MM
    dateFormat = mondayDate.toLocaleString({
      day: '2-digit',
      month: '2-digit',
    });
  } else {
    // Narrow: DD/MM
    dateFormat = mondayDate.toLocaleString({
      day: '2-digit',
      month: '2-digit',
    });
  }

  return `${prefix} ${dateFormat}`;
}

/**
 * Reusable component that wraps horizontally scrollable content with left/right arrow buttons.
 * Arrows are hidden when content doesn't overflow (no scrolling needed).
 */
function ScrollableWithArrows({
  children,
  scrollRef,
  onScrollLeft,
  onScrollRight,
  maxWidth,
  ariaLabelLeft = 'Scroll left',
  ariaLabelRight = 'Scroll right',
}) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showArrows, setShowArrows] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const hasOverflow = scrollWidth > clientWidth;

    setShowArrows(hasOverflow);
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial state
    updateScrollState();

    // Update on scroll
    el.addEventListener('scroll', updateScrollState);

    // Update on resize using ResizeObserver
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [scrollRef, updateScrollState]);

  // Shared styling for overlay arrow buttons
  const arrowButtonSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    bgcolor: 'background.paper',
    boxShadow: 1,
    transition: 'opacity 0.2s ease-in-out',
    '&:hover': {
      bgcolor: 'background.paper',
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
      }}
    >
      {/* Content area - full width */}
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>

      {/* Left arrow - overlaid */}
      {showArrows && (
        <IconButton
          onClick={onScrollLeft}
          disabled={!canScrollLeft}
          aria-label={ariaLabelLeft}
          size="small"
          sx={{
            ...arrowButtonSx,
            left: 4,
            opacity: canScrollLeft ? 0.9 : 0,
            pointerEvents: canScrollLeft ? 'auto' : 'none',
          }}
        >
          <ChevronLeft />
        </IconButton>
      )}

      {/* Right arrow - overlaid */}
      {showArrows && (
        <IconButton
          onClick={onScrollRight}
          disabled={!canScrollRight}
          aria-label={ariaLabelRight}
          size="small"
          sx={{
            ...arrowButtonSx,
            right: 4,
            opacity: canScrollRight ? 0.9 : 0,
            pointerEvents: canScrollRight ? 'auto' : 'none',
          }}
        >
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
}

/**
 * Week navigation bar component using MUI Tabs
 */
function WeekNavigationBar({
  weeks,
  colWidth,
  innerMaxWidth,
  onWeekClick,
  scrollbarWidth,
  selectedWeekIndex,
}) {
  const { t } = useTranslation();

  const handleTabChange = (event, newValue) => {
    onWeekClick(weeks[newValue], newValue);
  };

  return (
    <Box
      sx={{
        maxWidth: innerMaxWidth + scrollbarWidth,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tabs
        value={selectedWeekIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={t('week_navigation', 'Week navigation')}
        sx={{
          minHeight: 24,
          '& .MuiTab-root': {
            minHeight: 24,
            py: 0.5,
            px: 1.5,
            minWidth: 'auto',
            borderRadius: (theme) => `${theme.shape.borderRadius}px`,
            fontSize: '0.875rem',
            textTransform: 'none',
          },
        }}
      >
        {weeks.map((weekInfo) => (
          <Tab
            sx={{
              fontSize: (theme) => theme.typography.body2.fontSize,
            }}
            key={weekInfo.mondayStr}
            label={formatWeekButtonLabel(weekInfo.mondayDate, colWidth, t)}
            disabled={!weekInfo.hasAppointments}
          />
        ))}
      </Tabs>
    </Box>
  );
}

/**
 * Centered header for resource/product text labels.
 * Renders resource and product selectors centered, with a separator dot between them on desktop.
 */
function ContextHeader({
  displayResourceSelector,
  displayProductSelector,
  resourceElement,
  productElement,
  isSmallScreen,
}) {
  if (!displayResourceSelector && !displayProductSelector) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallScreen ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: isSmallScreen ? 0.5 : 1,
        mb: 1,
      }}
    >
      {displayResourceSelector && resourceElement}
      {displayResourceSelector && displayProductSelector && !isSmallScreen && (
        <Typography variant="body2" color="text.secondary">
          ·
        </Typography>
      )}
      {displayProductSelector && productElement}
    </Box>
  );
}

// Constants for gap calculations
const GAP_CONFIG = {
  vertical: {
    pxPer30Min: 5, // 5px per 30 minutes (linear scale)
    referenceHour: 8, // 8:00 AM reference for first bookable gap
  },
  horizontal: {
    minWidth: 8,
    maxWidth: 32,
    maxDays: 3, // Gaps beyond 3 days show maximum width
  },
  // FlickeringGrid styling for gaps
  gridStyle: {
    squareSize: 3,
    gridGap: 4,
    maxOpacity: 0.15,
    color: 'text.disabled',
  },
};

/**
 * Calculate vertical gap height for time distance between same-day timeslots
 * Uses linear scale: 5px per 30 minutes, no cap
 * @param {number} minutesDiff - Time difference in minutes
 * @returns {number} - Height in pixels
 */
function calculateVerticalGapHeight(minutesDiff) {
  if (minutesDiff <= 0) return 0;
  const { pxPer30Min } = GAP_CONFIG.vertical;

  // Linear scale: 5px per 30 minutes, no cap
  return Math.round((minutesDiff / 30) * pxPer30Min);
}

/**
 * Calculate horizontal gap width for time distance between different days
 * Uses logarithmic scale: 1 day -> 0px, 3 days -> maxWidth
 * @param {number} daysDiff - Time difference in days
 * @param {number} maxWidth - Maximum width in pixels (default from GAP_CONFIG)
 * @returns {number} - Width in pixels
 */
function calculateHorizontalGapWidth(
  daysDiff,
  maxWidth = GAP_CONFIG.horizontal.maxWidth,
) {
  if (daysDiff <= 1) return 0; // Adjacent days don't need gap
  const { minWidth, maxDays } = GAP_CONFIG.horizontal;

  // Consistent logarithmic scale: subtract 1 since we start from daysDiff > 1
  const adjustedDays = daysDiff - 1;
  const normalized = Math.log(1 + adjustedDays) / Math.log(1 + maxDays);
  const clamped = Math.min(normalized, 1);
  return Math.round(minWidth + clamped * (maxWidth - minWidth));
}

/**
 * Determines if there is a week boundary between two dates.
 * Uses ISO week numbering (weeks start on Monday) to ensure consistent
 * behavior across all locales.
 * @param {string|null} prevDate - Previous date in ISO format (YYYY-MM-DD)
 * @param {string} currentDate - Current date in ISO format (YYYY-MM-DD)
 * @returns {boolean} - True if dates are in different ISO weeks
 */
function isWeekBoundary(prevDate, currentDate) {
  if (!prevDate) return false;
  const prev = DateTime.fromISO(prevDate);
  const curr = DateTime.fromISO(currentDate);

  // Guard against invalid dates
  if (!prev.isValid || !curr.isValid) return false;

  return prev.weekNumber !== curr.weekNumber || prev.weekYear !== curr.weekYear;
}

/**
 * Renders a FlickeringGrid gap indicator for vertical gaps between timeslots
 * @param {number} gapHeight - Height of the gap in pixels
 * @returns {JSX.Element | null}
 */
function VerticalGapIndicator({ gapHeight }) {
  if (gapHeight <= 0) return null;
  return (
    <Grid item xs={12}>
      <Box
        sx={{
          height: gapHeight,
          width: '100%',
        }}
      ></Box>
      {/* <FlickeringGrid
        animated={false}
        height={gapHeight}
        fadeDirection="vertical"
        color={GAP_CONFIG.gridStyle.color}
        squareSize={GAP_CONFIG.gridStyle.squareSize}
        gridGap={GAP_CONFIG.gridStyle.gridGap}
        maxOpacity={GAP_CONFIG.gridStyle.maxOpacity}
        sx={{ width: '100%' }}
      /> */}
    </Grid>
  );
}

/**
 * Renders a single timeslot button (bookable, cancelable, or blocker).
 * Shared between standard and multi-resource modes.
 */
function TimeslotButton({ tsl, openBookingPage, colorStyle, fontSize, t }) {
  if (tsl.renderAsBookable || tsl.renderAsCancelable) {
    return (
      <Button
        fullWidth
        variant="outlined"
        onClick={() => openBookingPage(tsl)}
        sx={{
          minWidth: 0,
          height: '100%',
          ...(fontSize && { fontSize }),
          ...(colorStyle && {
            backgroundColor: colorStyle.bg,
            borderColor: colorStyle.border,
            '&:hover': {
              backgroundColor: colorStyle.border,
              borderColor: colorStyle.border,
            },
          }),
          ...(fontSize && { px: 0.5 }),
        }}
      >
        {tsl.start.toLocaleString({
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Button>
    );
  } else if (tsl.renderAsBlocker) {
    return (
      <Button
        variant="text"
        fullWidth
        disabled
        sx={{
          textTransform: 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          height: '100%',
          border: (theme) => `1px dashed ${theme.palette.divider}`,
          ...(fontSize && { fontSize }),
          ...(colorStyle && {
            backgroundColor: colorStyle.bg,
          }),
          ...(fontSize && { px: 0.5 }),
        }}
      >
        {t('appoinment_at_capacity')}
      </Button>
    );
  }
  return null;
}

/**
 * Calculates the vertical gap before a timeslot or group.
 */
function calculateGapMinutes(index, current, previous) {
  if (index > 0 && previous?.end && current?.start) {
    return Math.max(0, current.start.diff(previous.end, 'minutes').minutes);
  } else if (index === 0 && current?.start?.isValid) {
    const referenceTime = current.start.set({
      hour: GAP_CONFIG.vertical.referenceHour,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    if (current.start > referenceTime) {
      return current.start.diff(referenceTime, 'minutes').minutes;
    }
  }
  return 0;
}

function Column({
  date,
  timeslots,
  width,
  openBookingPage,
  multiResourceMode,
  colorMap,
}) {
  const { t } = useTranslation();
  const title = useMemo(() => {
    const dateTime = DateTime.fromISO(date);
    const weekday = dateTime.toLocaleString({ weekday: 'short' });
    const dayMonth = dateTime.toLocaleString({
      day: '2-digit',
      month: '2-digit',
    });
    return `${weekday} ${dayMonth}`;
  }, [date]);

  const overlapGroups = useMemo(() => {
    if (!multiResourceMode || !timeslots) return null;
    return groupOverlapping(timeslots);
  }, [multiResourceMode, timeslots]);

  if (!timeslots) return <></>;

  /*
    See here for an indepth explanation of when it is okay to use index as react list keys:
    https://stackoverflow.com/questions/59517962/react-using-index-as-key-for-items-in-the-list
  */

  return (
    <Grid
      key={date}
      container
      item
      xs="auto"
      sx={{
        alignContent: 'flex-start',
      }}
    >
      <Grid
        item
        xs={12}
        sx={{
          position: 'sticky',
          py: 0.5,
          top: 0,
          // do not remove. The correct way to make this work for differently colored backgrounds
          // is to adapt the paper color in the mui theme.
          backgroundColor: (theme) => theme.palette.background.paper,
          zIndex: 1,
        }}
      >
        <Typography sx={{ textAlign: 'center' }} variant="body2">
          {title}
        </Typography>
      </Grid>
      <Grid
        container
        item
        xs
        direction={'row'}
        rowSpacing={1}
        sx={{
          alignItems: 'center',
          minWidth: `${width - 8}px !important`,
          maxWidth: `${width - 8}px !important`,
          mt: 1,
        }}
      >
        {multiResourceMode && overlapGroups
          ? overlapGroups.map((group, groupIndex) => {
              // Calculate gap: distance between previous group's latest end and this group's earliest start
              const prevGroup =
                groupIndex > 0 ? overlapGroups[groupIndex - 1] : null;
              const prevLatestEnd = prevGroup
                ? prevGroup.reduce(
                    (latest, tsl) =>
                      !latest || tsl.end > latest ? tsl.end : latest,
                    null,
                  )
                : null;
              const currentEarliestStart = group[0]?.start;

              const minutesDiff = calculateGapMinutes(
                groupIndex,
                { start: currentEarliestStart },
                { end: prevLatestEnd },
              );
              const gapHeight = calculateVerticalGapHeight(minutesDiff);

              const columns = subGroupByUuid(group);
              // Font size scales with number of side-by-side columns, not total timeslots,
              // because stacked items within a column do not reduce horizontal space.
              const fontSize =
                columns.length > 1
                  ? adaptiveFontSize(columns.length)
                  : undefined;

              const groupKey =
                group
                  .map((tsl) => tsl.uuid || tsl.timeslot_uuid || groupIndex)
                  .join('-') + `-g${groupIndex}`;

              return (
                <React.Fragment key={groupKey}>
                  <VerticalGapIndicator gapHeight={gapHeight} />
                  <Grid item xs={12}>
                    {columns.length === 1 && columns[0].length === 1 ? (
                      <TimeslotButton
                        tsl={columns[0][0]}
                        openBookingPage={openBookingPage}
                        colorStyle={
                          colorMap
                            ? colorMap[columns[0][0].resource_uuid]
                            : undefined
                        }
                        t={t}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '2px',
                          width: '100%',
                        }}
                      >
                        {columns.map((column, colIndex) => (
                          <Box
                            key={
                              column[0].uuid ||
                              column[0].timeslot_uuid ||
                              `col-${colIndex}`
                            }
                            sx={{
                              flex: '1 1 0',
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                            }}
                          >
                            {column.map((tsl, tslIndex) => (
                              <Box
                                key={
                                  tsl.uuid +
                                  '-' +
                                  tsl.resource_uuid +
                                  '-' +
                                  tslIndex
                                }
                                sx={{ flex: '1 1 0' }}
                              >
                                <TimeslotButton
                                  tsl={tsl}
                                  openBookingPage={openBookingPage}
                                  colorStyle={
                                    colorMap
                                      ? colorMap[tsl.resource_uuid]
                                      : undefined
                                  }
                                  fontSize={fontSize}
                                  t={t}
                                />
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Grid>
                </React.Fragment>
              );
            })
          : timeslots.map((tsl, index) => {
              // Standard (non-multi-resource) rendering
              const prevTimeslot = index > 0 ? timeslots[index - 1] : null;
              const minutesDiff = calculateGapMinutes(index, tsl, prevTimeslot);
              const gapHeight = calculateVerticalGapHeight(minutesDiff);

              return (
                <React.Fragment key={index}>
                  <VerticalGapIndicator gapHeight={gapHeight} />
                  <Grid item xs={12}>
                    <TimeslotButton
                      tsl={tsl}
                      openBookingPage={openBookingPage}
                      t={t}
                    />
                  </Grid>
                </React.Fragment>
              );
            })}
      </Grid>
    </Grid>
  );
}

/**
 * Displays bookables in a vertical list.
 * @param {} param0
 * @returns
 */
export default function CondensedView(props) {
  const { t } = useTranslation();
  const {
    isFetching,
    bookables,
    products,
    product,
    resource,
    publicDataMap,
    openResourcePage,
    openProductPage,
    openBookingPage,
    height,
    suppressDefaultInterfaces = false,
  } = usePropsBeforeContext(props, CalendarViewContext);

  const [headerRef, headerDimensions] = useElDimensions(undefined, 'outer');
  const [containerRef, containerDimensions] = useElDimensions(
    undefined,
    'outer',
  );
  const { width: windowWidth } = useWindowDimensions();
  const listRef = React.useRef(null);
  const [colWidthBase] = React.useState(120);
  const [colWidth, setColWidth] = React.useState(colWidthBase);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const selectedWeekIndexRef = useRef(selectedWeekIndex);

  // Keep ref in sync with state
  useEffect(() => {
    selectedWeekIndexRef.current = selectedWeekIndex;
  }, [selectedWeekIndex]);

  const calendarFrontendOptions = useSelector((state) =>
    selectAppConfigProp(state, 'calendarFrontendOptions'),
  );
  const multiResourceMode =
    calendarFrontendOptions?.condensedView?.multiResourceMode || false;
  const scrollbarWidth = useScrollbarWidth({ width: 'thin' });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const parsedBookables = useBookableCurator({
    bookables,
  });

  const activeResourceCount = useMemo(() => {
    const uuids = new Set();
    for (const [, timeslots] of parsedBookables) {
      for (const tsl of timeslots) {
        if (tsl.resource_uuid) uuids.add(tsl.resource_uuid);
      }
    }
    return uuids.size;
  }, [parsedBookables]);

  const colorMap = useMemo(() => {
    if (!multiResourceMode || !publicDataMap || activeResourceCount <= 1)
      return null;
    return buildColorMap(
      publicDataMap,
      theme.palette.primary.main,
      theme.palette.mode,
    );
  }, [
    multiResourceMode,
    publicDataMap,
    activeResourceCount,
    theme.palette.primary.main,
    theme.palette.mode,
  ]);

  const shouldDisplayResourceSelect = useCallback(() => {
    return (
      openResourcePage &&
      (isSmallScreen ||
        calendarFrontendOptions?.condensedView?.forceResourceSelectorDialog) &&
      publicDataMap &&
      Object.keys(publicDataMap).length > 1
    );
  }, [
    calendarFrontendOptions?.condensedView?.forceResourceSelectorDialog,
    isSmallScreen,
    openResourcePage,
    publicDataMap,
  ]);

  const viewportWidth = useMemo(() => {
    // meaning there are multiple resources but forceResourceSelectorDialog is disabled
    if (
      !isSmallScreen &&
      publicDataMap &&
      Object.keys(publicDataMap).length > 1 &&
      !shouldDisplayResourceSelect()
    ) {
      return windowWidth - (240 + scrollbarWidth);
    } else {
      return windowWidth - scrollbarWidth;
    }
  }, [
    isSmallScreen,
    publicDataMap,
    scrollbarWidth,
    shouldDisplayResourceSelect,
    windowWidth,
  ]);

  const maxColumns = useMemo(() => {
    return Math.min(
      Math.max(
        Math.floor(viewportWidth / colWidth),
        1 /* show a minimum of 1 day */,
      ),
      5, // show a maximum of 5 days
    );
  }, [colWidth, viewportWidth]);

  // max width for container of header and content
  const outerMaxWidth = useMemo(() => {
    return viewportWidth;
  }, [viewportWidth]);

  // max width for content
  const innerMaxWidth = useMemo(() => {
    if (viewportWidth < maxColumns * colWidth) {
      return viewportWidth;
    } else {
      return maxColumns * colWidth;
    }
  }, [colWidth, maxColumns, viewportWidth]);

  /**
   * Calculate all weeks from first to last appointment date
   */
  const weeks = useMemo(() => {
    if (parsedBookables.length === 0) return [];

    // Get first and last dates from parsedBookables
    const firstDate = DateTime.fromISO(parsedBookables[0][0]);
    const lastDate = DateTime.fromISO(
      parsedBookables[parsedBookables.length - 1][0],
    );

    // Get Monday of the first week and Monday of the last week
    const firstMonday = firstDate.startOf('week');
    const lastMonday = lastDate.startOf('week');

    // Create a set of all dates that have appointments for quick lookup
    const datesWithAppointments = new Set(
      parsedBookables.map(([date]) => date),
    );

    const result = [];
    let currentMonday = firstMonday;

    while (currentMonday <= lastMonday) {
      const mondayStr = currentMonday.toISODate();
      const weekEnd = currentMonday.plus({ days: 6 });

      // Check if any date in this week has appointments
      let hasAppointments = false;
      let firstAvailableDay = null;

      for (let d = currentMonday; d <= weekEnd; d = d.plus({ days: 1 })) {
        const dateStr = d.toISODate();
        if (datesWithAppointments.has(dateStr)) {
          hasAppointments = true;
          if (firstAvailableDay === null) {
            firstAvailableDay = dateStr;
          }
        }
      }

      result.push({
        mondayDate: currentMonday,
        mondayStr,
        hasAppointments,
        firstAvailableDay,
      });

      currentMonday = currentMonday.plus({ weeks: 1 });
    }

    return result;
  }, [parsedBookables]);

  // Initialize selectedWeekIndex based on first visible bookable
  useEffect(() => {
    if (weeks.length > 0 && parsedBookables.length > 0) {
      const firstDate = parsedBookables[0][0];
      const firstDateTime = DateTime.fromISO(firstDate);

      const initialWeekIndex = weeks.findIndex((week) => {
        const weekEnd = week.mondayDate.plus({ days: 6 });
        return firstDateTime >= week.mondayDate && firstDateTime <= weekEnd;
      });

      if (initialWeekIndex !== -1) {
        setSelectedWeekIndex(initialWeekIndex);
      }
    }
  }, [weeks, parsedBookables]);

  // Reset selectedWeekIndex if it becomes out of bounds
  useEffect(() => {
    if (selectedWeekIndex >= weeks.length && weeks.length > 0) {
      setSelectedWeekIndex(weeks.length - 1);
    }
  }, [weeks.length, selectedWeekIndex]);

  // Precompute cumulative horizontal offsets for each date column.
  // Replays the same gap/divider logic used in the rendered JSX so that
  // scrollToWeek and scroll-sync can map between index and scroll position
  // without hardcoded estimates.
  const columnOffsets = useMemo(() => {
    const spacingUnit = parseInt(theme.spacing(1), 10) || 8;
    const dividerOnlyWidth = 2 * spacingUnit + 1; // m:1 each side + 1px line

    const offsets = [];
    let cumulative = 0;

    for (let i = 0; i < parsedBookables.length; i++) {
      const date = parsedBookables[i][0];
      const prevDate = i > 0 ? parsedBookables[i - 1][0] : null;

      if (prevDate) {
        const daysDiff = Math.round(
          DateTime.fromISO(date).diff(DateTime.fromISO(prevDate), 'days').days,
        );
        const gapWidth = calculateHorizontalGapWidth(daysDiff);
        const isNewWeek = isWeekBoundary(prevDate, date);

        if (isNewWeek && gapWidth > 0) {
          cumulative += gapWidth;
        } else if (isNewWeek) {
          cumulative += dividerOnlyWidth;
        } else if (gapWidth > 0) {
          cumulative += gapWidth;
        }
        // else: adjacent days, no spacer, cumulative unchanged
      }

      offsets.push(cumulative);
      cumulative += colWidth; // the column itself
    }

    return offsets;
  }, [parsedBookables, colWidth, theme]);

  /**
   * Find the index of the first column visible at the current scroll position.
   */
  const findVisibleColumnIndex = useCallback(() => {
    const el = listRef.current;
    if (!el || columnOffsets.length === 0) return 0;
    const scrollPos = el.scrollLeft;
    // Find the last offset that is <= scrollPos (i.e. the leftmost visible column)
    let idx = 0;
    for (let i = 1; i < columnOffsets.length; i++) {
      if (columnOffsets[i] > scrollPos + 1) break; // +1 tolerance for rounding
      idx = i;
    }
    return idx;
  }, [columnOffsets]);

  const handleScrollLeft = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const currentIdx = findVisibleColumnIndex();
    const step = Math.max(maxColumns - 1, 1);
    const targetIdx = Math.max(currentIdx - step, 0);
    el.scrollTo({ left: columnOffsets[targetIdx], behavior: 'smooth' });
  }, [findVisibleColumnIndex, maxColumns, columnOffsets]);

  const handleScrollRight = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const currentIdx = findVisibleColumnIndex();
    const step = Math.max(maxColumns - 1, 1);
    const targetIdx = Math.min(
      currentIdx + step,
      columnOffsets.length - 1,
    );
    el.scrollTo({ left: columnOffsets[targetIdx], behavior: 'smooth' });
  }, [findVisibleColumnIndex, maxColumns, columnOffsets]);

  /**
   * Scrolls to the specified week and updates the selected tab
   */
  const scrollToWeek = useCallback(
    (weekInfo, weekIndex) => {
      // Update selected week index
      if (weekIndex !== undefined) {
        setSelectedWeekIndex(weekIndex);
      } else {
        // Find the week index if not provided
        const foundIndex = weeks.findIndex(
          (w) => w.mondayStr === weekInfo.mondayStr,
        );
        if (foundIndex !== -1) {
          setSelectedWeekIndex(foundIndex);
        }
      }

      if (!listRef.current) return;

      // Find the target date
      let targetDate = weekInfo.firstAvailableDay;

      if (!targetDate) {
        // Find first date in parsedBookables that falls within this week
        const weekStart = weekInfo.mondayDate;
        const weekEnd = weekInfo.mondayDate.plus({ days: 6 });

        for (const [date] of parsedBookables) {
          const d = DateTime.fromISO(date);
          if (d >= weekStart && d <= weekEnd) {
            targetDate = date;
            break;
          }
        }
      }

      if (!targetDate) return;

      // Find the index of this date in parsedBookables
      const targetIndex = parsedBookables.findIndex(
        ([date]) => date === targetDate,
      );

      if (targetIndex === -1) return;

      // Use precomputed column offsets for accurate scroll position
      const scrollTarget = columnOffsets[targetIndex] ?? targetIndex * colWidth;

      listRef.current.scrollTo({
        left: scrollTarget,
        behavior: 'smooth',
      });
    },
    [parsedBookables, colWidth, weeks, columnOffsets],
  );

  useEffect(() => {
    const filledColumns = Math.min(parsedBookables.length, 5);
    // Calculate column width based on actual container width
    // This ensures columns fill the available space without overlapping
    if (filledColumns === 0 || !containerDimensions?.width) {
      setColWidth(colWidthBase);
      return;
    }

    // Use container width instead of viewport width
    const availableWidth = containerDimensions.width - scrollbarWidth;

    // Calculate the ideal width per column based on container
    const idealColWidth = Math.floor(availableWidth / filledColumns);

    // Clamp between minimum (colWidthBase) and maximum (colWidthBase * 2)
    const clampedWidth = Math.min(
      Math.max(idealColWidth, colWidthBase),
      colWidthBase * 2,
    );

    setColWidth(clampedWidth);
  }, [
    colWidthBase,
    parsedBookables.length,
    containerDimensions?.width,
    scrollbarWidth,
  ]);

  // Sync selected week tab with horizontal scroll position
  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl || weeks.length === 0) return;

    let rafId = null;

    const handleScroll = () => {
      // Throttle with requestAnimationFrame
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        // Find the first visible date column using precomputed offsets
        const visibleIndex = findVisibleColumnIndex();

        if (visibleIndex >= 0 && visibleIndex < parsedBookables.length) {
          const visibleDate = parsedBookables[visibleIndex][0];
          const visibleDateTime = DateTime.fromISO(visibleDate);

          // Find which week this date belongs to
          const weekIndex = weeks.findIndex((week) => {
            const weekEnd = week.mondayDate.plus({ days: 6 });
            return (
              visibleDateTime >= week.mondayDate && visibleDateTime <= weekEnd
            );
          });

          // Use ref to get current value without triggering effect re-runs
          if (weekIndex !== -1 && weekIndex !== selectedWeekIndexRef.current) {
            setSelectedWeekIndex(weekIndex);
          }
        }
      });
    };

    listEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      listEl.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [weeks, parsedBookables, colWidth, columnOffsets, findVisibleColumnIndex]);

  // find longest list
  let maxLength = 0;
  for (const entry of parsedBookables) {
    for (const timeslots of Object.values(entry)) {
      timeslots.length > maxLength ? (maxLength = timeslots.length) : null;
    }
  }

  return (
    <CrossFade
      showSkeleton={isFetching || (!product && products?.length > 0)}
      graceMs={LOADING_GRACE_MS}
      minDisplayMs={LOADING_MIN_DISPLAY_MS}
      skeleton={<LoadingSkeleton height={height} />}
      wrapperProps={{
        sx: {
          height: height,
        },
      }}
    >
      <Grid
        id="calendar-view-condensed"
        ref={containerRef}
        xs
        item
        container
        sx={{
          justifyContent: 'center',
          maxWidth: `${outerMaxWidth}px !important`,
          mx: 'auto',
        }}
      >
        <Grid ref={headerRef} item xs={12}>
          <ContextHeader
            displayResourceSelector={shouldDisplayResourceSelect()}
            displayProductSelector={!!openProductPage}
            isSmallScreen={isSmallScreen}
            resourceElement={
              <ResourceSelectorLabel
                resource={resource}
                onClick={openResourcePage}
              />
            }
            productElement={
              <ProductSelectorLabel
                onClick={openProductPage}
                product={product}
                products={products}
              />
            }
          />
          {multiResourceMode && colorMap && (
            <ResourceColorLegend
              colorMap={colorMap}
              publicDataMap={publicDataMap}
            />
          )}
        </Grid>

        {parsedBookables.length > 0 && weeks.length > 1 && (
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}
          >
            <WeekNavigationBar
              weeks={weeks}
              colWidth={colWidth}
              innerMaxWidth={innerMaxWidth}
              onWeekClick={scrollToWeek}
              scrollbarWidth={scrollbarWidth}
              selectedWeekIndex={selectedWeekIndex}
            />
          </Grid>
        )}
        <Grid
          item
          container
          xs={12}
          sx={{
            justifyContent:
              parsedBookables.length === 0 ? 'center' : 'flex-start',
          }}
        >
          {parsedBookables.length === 0 && (
            <Grid
              item
              sx={{
                alignSelf: 'center',
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  color: (theme) => theme.palette.text.secondary,
                }}
              >
                {t('noEventsMessage')}
              </Typography>
            </Grid>
          )}
          {parsedBookables.length > 0 && (
            <Grid
              item
              container
              xs
              sx={{
                justifyContent: 'center',
              }}
            >
              <ScrollableWithArrows
                scrollRef={listRef}
                onScrollLeft={handleScrollLeft}
                onScrollRight={handleScrollRight}
                maxWidth={innerMaxWidth + scrollbarWidth}
                ariaLabelLeft={t('scroll_days_left', 'Scroll days left')}
                ariaLabelRight={t('scroll_days_right', 'Scroll days right')}
              >
                <Box
                  id="calendar-view-condensed-list"
                  ref={listRef}
                  sx={{
                    width: '100%',
                    overflow: 'auto',
                    minHeight: '200px',
                    maxHeight: height - headerDimensions.height,
                    scrollbarWidth: 'thin',
                  }}
                >
                  <List
                    sx={{
                      pt: 0,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent:
                        parsedBookables.length >= maxColumns
                          ? 'flex-start'
                          : 'center',
                      alignItems: 'flex-start',
                    }}
                  >
                    {parsedBookables.map(([date, timeslots], index) => {
                      const prevDate =
                        index > 0 ? parsedBookables[index - 1][0] : null;

                      // Calculate days between dates for horizontal gap
                      const daysDiff = prevDate
                        ? Math.round(
                            DateTime.fromISO(date).diff(
                              DateTime.fromISO(prevDate),
                              'days',
                            ).days,
                          )
                        : 0;
                      const gapWidth = calculateHorizontalGapWidth(daysDiff);
                      const isNewWeek = isWeekBoundary(prevDate, date);

                      return (
                        <React.Fragment key={date}>
                          {/* Case 1: Week boundary with gap - render gap with centered divider */}
                          {isNewWeek && gapWidth > 0 && (
                            <Box
                              sx={{
                                width: `${gapWidth}px`,
                                minWidth: `${gapWidth}px`,
                                display: 'flex',
                                justifyContent: 'center',
                                alignSelf: 'stretch',
                                my: 1,
                              }}
                            >
                              <Divider orientation="vertical" flexItem />
                            </Box>
                          )}
                          {/* Case 2: Week boundary without gap - render just the divider */}
                          {isNewWeek && gapWidth === 0 && (
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{ alignSelf: 'stretch', m: 1 }}
                            />
                          )}
                          {/* Case 3: No week boundary but has gap - render just the gap */}
                          {!isNewWeek && gapWidth > 0 && (
                            <Box
                              sx={{
                                alignSelf: 'stretch',
                                width: `${gapWidth}px`,
                                minWidth: `${gapWidth}px`,
                                minHeight: '100px',
                                my: 1,
                              }}
                            />
                          )}
                          <ListItem
                            id={date}
                            sx={{
                              px: 2,
                              justifyContent: 'center',
                              flexGrow: 0,
                              flexBasis: 'auto',
                              width: `${colWidth}px`,
                              flexShrink: 0,
                            }}
                          >
                            <motion.div
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0 }}
                              viewport={{ root: listRef }}
                              transition={{ duration: 0.3 }}
                              amount="all"
                              layout="position"
                            >
                              <Column
                                date={date}
                                timeslots={timeslots}
                                openBookingPage={openBookingPage}
                                width={colWidth}
                                multiResourceMode={multiResourceMode}
                                colorMap={colorMap}
                              />
                            </motion.div>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Box>
              </ScrollableWithArrows>
            </Grid>
          )}
        </Grid>
      </Grid>
      {!suppressDefaultInterfaces && <DefaultInterfacesHandler />}
    </CrossFade>
  );
}

// Header height estimate: Header (~32px) + Week nav (~36px) + margins (~32px)
const SKELETON_HEADER_HEIGHT_ESTIMATE = 100;

const LoadingSkeleton = ({ height }) => {
  // Show 5 skeleton columns to represent typical loading state
  const skeletonColumns = [1, 2, 3, 4, 5];
  const colWidth = 120; // Base column width matching actual component
  const timeSlots = 6; // Number of time slot placeholders per column

  return (
    <Box
      id="condensed-view-loading-skeleton"
      role="status"
      aria-busy="true"
      aria-label="Loading calendar view"
      sx={{
        height: height,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Header placeholder - centered like ContextHeader */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Skeleton variant="rounded" width={120} height={32} />
        <Skeleton variant="text" width={8} height={32} />
        <Skeleton variant="rounded" width={100} height={32} />
      </Box>

      {/* Week navigation placeholder */}
      <Box sx={{ mb: 1 }}>
        <Skeleton variant="rounded" width={400} height={36} />
      </Box>

      {/* Column list - horizontal layout matching actual content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: 0,
          overflow: 'hidden',
          height: height ? height - SKELETON_HEADER_HEIGHT_ESTIMATE : undefined,
        }}
      >
        {skeletonColumns.map((col) => (
          <Box
            key={col}
            sx={{
              width: `${colWidth}px`,
              minWidth: `${colWidth}px`,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Day header */}
            <Skeleton
              variant="text"
              width={colWidth - 16}
              height={25}
              sx={{ mb: 1 }}
            />
            {/* Time slots */}
            {Array.from({ length: timeSlots }).map((_, idx) => (
              <Skeleton
                key={idx}
                variant="rounded"
                width={colWidth - 24}
                height={36}
                sx={{ mt: 1 }}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
